<script lang="ts">
	import calendarIcon from "@core/assets/icons/calendar.svg?raw";
	import type { Procedure } from "@core/schema/Procedure.js";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { getProcedureDetailPath } from "@core/schema/paths/getProcedureDetailPath.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import Formatted from "../common/Formatted.svelte";
	import Icon from "../common/Icon.svelte";
	import MediaWithHoverVideo from "../common/MediaWithHoverVideo.svelte";
	import Modal from "../common/Modal.svelte";
	import Booking from "../home/Booking.svelte";
	import Faq from "../home/Faq.svelte";
	import VoucherOrderForm from "./VoucherOrderForm.svelte";

	interface Props {
		procedures: Procedure[];
	}

	const { procedures }: Props = $props();
	const { tenant } = getGlobalTemplateProps();
	const { FILE_BUCKET_URL_MAP } = getGlobalSettings();

	let showOrderModal = $state(false);
	let selectedProcedureTitle = $state<string>("");

	function checkHash(): void {
		if (window.location.hash === "#objednat") {
			showOrderModal = true;
			history.replaceState(null, "", window.location.pathname);
		}
	}

	$effect(() => {
		checkHash();
		window.addEventListener("hashchange", checkHash);
		return (): void => {
			window.removeEventListener("hashchange", checkHash);
		};
	});

	function openOrderModal(procedureTitle: string): void {
		selectedProcedureTitle = procedureTitle;
		showOrderModal = true;
	}

	function getVideoUrl(video: unknown): string | undefined {
		const file = video as UploadedFile | undefined;
		if (!file) {
			return undefined;
		}
		return getUploadedFilePath(file, FILE_BUCKET_URL_MAP);
	}

	function formatDuration(minutes: number): string {
		return `(${minutes} min)`;
	}

	function formatPrice(price: number): string {
		return `${price.toLocaleString("cs-CZ")} Kč`;
	}

	function getFirstImageUrl(images: unknown[]): string | null {
		if (images.length > 0) {
			return getUploadedFilePath(images[0] as UploadedFile, FILE_BUCKET_URL_MAP);
		}
		return null;
	}

	// Take first 3 visible procedures, reorder so main is in center
	const featuredProcedures = $derived.by((): (Procedure & { isMainFeatured: boolean })[] => {
		const visible = procedures.filter((p) => p.visible);
		const first3 = visible.slice(0, 3);
		if (first3.length >= 3) {
			return [
				{ ...first3[1], isMainFeatured: false },
				{ ...first3[0], isMainFeatured: true },
				{ ...first3[2], isMainFeatured: false },
			] as (Procedure & { isMainFeatured: boolean })[];
		}
		return first3.map((proc, index) => ({
			...proc,
			isMainFeatured: index === 0,
		}));
	});

	interface VideoComponent {
		play(): void;
		stop(): void;
	}
	const videoRefs: Record<number, VideoComponent | undefined> = {};

	function handleCardMouseEnter(index: number): void {
		const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
		const ref = videoRefs[index];
		if (!isMobile && ref !== undefined) {
			ref.play();
		}
	}

	function handleCardMouseLeave(index: number): void {
		const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
		const ref = videoRefs[index];
		if (!isMobile && ref !== undefined) {
			ref.stop();
		}
	}
</script>

<section class="flex flex-col justify-center items-center gap-8 sm:gap-12 md:gap-16 full-width">
	<div class="flex flex-col justify-center items-center gap-8 sm:gap-12 md:gap-16 w-full container-center">
		<h1 class="text-center w-full max-w-[57.5rem] px-4">
			<Formatted
				text="
		Obdarujte svou drahou polovičku relaxem"
			/>
			<span class="inline-block">
				<Formatted text="a péčí" />
				<span class="relative inline-block">
					napohodu
					<span class="absolute bottom-0 left-0 h-1 w-full bg-current"></span>
				</span>
			</span>
		</h1>

		<div class="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-4 justify-center items-center md:items-center">
			{#each featuredProcedures as procedure, index (procedure.id)}
				{@const imageUrl = getFirstImageUrl(procedure.images)}
				{@const videoUrl = getVideoUrl(procedure.video)}
				{@const procTitle = procedure.title.replace("nl[[]]", " ")}
				<div
					class="flex flex-col items-center {procedure.isMainFeatured
						? 'pt-4 pb-16 px-4 gap-6 md:gap-10 w-full max-w-[18rem] md:min-w-[20rem] md:max-w-[20rem] xl:min-w-[26.25rem] xl:max-w-[26.25rem] bg-[#604339] text-ivory-100 shadow-[0px_16px_16px_-16px_rgba(0,0,0,0.1)]'
						: 'pt-4 pb-16 px-4 gap-4 md:gap-6 w-full max-w-[18rem] md:min-w-[17.5rem] md:max-w-[17.5rem] xl:min-w-[22.875rem] xl:max-w-[22.875rem] bg-ivory-100 text-hazelnut-500 border border-[#E6D9CC] shadow-[0px_24px_8px_-16px_rgba(0,0,0,0.05)]'} rounded-[8rem] md:rounded-[10rem] {procedure.isMainFeatured
						? 'xl:rounded-[13.125rem]'
						: 'xl:rounded-[11.875rem]'}"
					role="group"
					onmouseenter={(): void => {
						handleCardMouseEnter(index);
					}}
					onmouseleave={(): void => {
						handleCardMouseLeave(index);
					}}
				>
					<a
						href={getProcedureDetailPath(tenant.locale, procedure.urlSlug)}
						class="block w-full aspect-square rounded-full overflow-hidden flex-none self-stretch"
					>
						<MediaWithHoverVideo
							bind:this={videoRefs[index]}
							imageSrc={imageUrl ?? ""}
							videoSrc={videoUrl}
							alt={procedure.title}
							imageWidth={procedure.isMainFeatured ? 388 : 334}
							imageHeight={procedure.isMainFeatured ? 388 : 334}
							class="w-full h-full object-cover"
							wrapperClass="w-full h-full rounded-full overflow-hidden"
							playOnScroll={true}
							eager
						/>
					</a>
					<div
						class="flex flex-col items-center px-4 {procedure.isMainFeatured
							? 'gap-4 md:gap-10'
							: 'gap-4 md:gap-8'} flex-none self-stretch"
					>
						<h3
							class="font-heading text-base md:text-[2rem] font-bold text-center leading-[1.1] md:leading-[110%] flex-none self-stretch {procedure.isMainFeatured
								? 'text-[#F7F3EF]'
								: 'text-[#4F372F]'}"
						>
							<a href={getProcedureDetailPath(tenant.locale, procedure.urlSlug)} class="no-underline hover:underline">
								<Formatted text={procedure.title} />
								<br />
								<span class="inline-block">
									{formatDuration(procedure.duration)}
								</span>
							</a>
						</h3>

						<p
							class="text-xs md:text-[1rem] md:leading-[140%] font-medium text-center flex-none self-stretch {procedure.isMainFeatured
								? 'text-[#F7F3EF]'
								: 'text-[#21212F]'}"
						>
							<Formatted text={procedure.description} />
						</p>
						<div
							class="flex flex-wrap justify-center items-start content-center gap-1 w-full max-w-[20.625rem] flex-none"
						>
							{#each procedure.tags.slice(0, 8) as tag, tagIndex (tag)}
								<span
									class="justify-center items-center px-2 md:px-3 py-0.5 md:py-1 rounded-[2rem] md:rounded-[2.5rem] text-[10px] md:text-xs font-medium leading-[0.875rem] md:leading-[0.9375rem] uppercase border md:border-[1.5px] border-[#F7CFCF] flex-none {procedure.isMainFeatured
										? 'text-[#F7F3EF]'
										: 'text-[#21212F]'} {tagIndex >= 5 ? 'hidden lg:flex' : 'flex'}"
								>
									<Formatted text={tag} />
								</span>
							{/each}
							{#if procedure.tags.length > 5}
								<span
									class="flex lg:hidden justify-center items-center px-2 py-0.5 rounded-[2rem] text-[10px] font-medium leading-[0.875rem] border border-[#F7CFCF] flex-none {procedure.isMainFeatured
										? 'text-[#F7F3EF]'
										: 'text-[#21212F]'}"
								>
									a další…
								</span>
							{/if}
							{#if procedure.tags.length > 8}
								<span
									class="hidden lg:flex justify-center items-center md:px-3 md:py-1 md:rounded-[2.5rem] md:text-xs font-medium md:leading-[0.9375rem] md:border-[1.5px] border-[#F7CFCF] flex-none {procedure.isMainFeatured
										? 'text-[#F7F3EF]'
										: 'text-[#21212F]'}"
								>
									a další…
								</span>
							{/if}
						</div>

						<div class="flex flex-col items-center gap-2 flex-none self-stretch">
							<p
								class="text-sm md:text-[1.5rem] md:leading-[2.25rem] font-bold text-center flex-none {procedure.isMainFeatured
									? 'text-[#F7F3EF]'
									: 'text-[#21212F]'}"
							>
								{formatPrice(procedure.price)}
							</p>
							<button
								type="button"
								onclick={(): void => {
									openOrderModal(procTitle);
								}}
								class="inline-flex flex-row justify-center items-center uppercase px-4 h-8 pt-0.5 gap-2.5 duration-300 bg-copper-300 hover:bg-copper-200 shadow-[0px_6px_2px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0px_6px_8px_-4px_rgba(0,0,0,0.4)] rounded-2xl text-xs text-ivory-100 transition-all font-medium tracking-[0.05em]"
							>
								<Icon icon={calendarIcon} />
								objednat voucher
							</button>
						</div>
					</div>
				</div>
			{/each}
		</div>
	</div>
</section>

<Modal bind:show={showOrderModal}>
	<div class="flex flex-col gap-4 md:gap-6 w-full max-w-md">
		<div class="text-center">
			<h2 class="font-heading text-xl md:text-3xl font-bold text-hazelnut mb-1 md:mb-2">Objednávka voucheru</h2>
			{#if selectedProcedureTitle}
				<p class="text-copper-300 font-semibold text-base md:text-lg hidden md:block">{selectedProcedureTitle}</p>
			{/if}
		</div>
		<VoucherOrderForm {procedures} {selectedProcedureTitle} />
	</div>
</Modal>

<Booking style="voucher" />

<Faq page="vouchers" />
